// Единый чёрный список по всей группе юридических лиц (тз 5.8.2, сценарии 89 и 90).
//
// Одна строка — одна грамматика (правило 2): кто в записи, почему он в ней и до какого числа
// она хранится. Над списком — сколько записей в ЭТОЙ выборке, числом из ответа сервера, а не
// длиной показанного куска.
//
// ## Снятая запись и уничтоженная — РАЗНЫЕ вещи, и разница обязана быть видна
//
// Снятие оставляет запись со всей историей и добавляет к ней дату, автора и причину: человек
// снова проходит сверку, но повод остаётся. Уничтожение стирает персональные данные насовсем.
// Это два разных ответа на вопрос «почему его больше не находит сверка», и сливать их в одно
// серое «нет» нельзя: от разницы зависит, можно ли этого человека нанимать.
//
// Поэтому снятая запись остаётся в списке со своей меткой, а запись без персональных данных
// подписывается словами. Пустая строка на месте фамилии читалась бы как «не догрузилось»
// (правило 7: пустое, нулевое и неизвестное выглядят по-разному).

import { useEffect, useRef, useState } from 'react';
import { RiAddLine, RiUserForbidLine } from '@remixicon/react';
import { Link, useNavigate } from 'react-router';

import { ListPane } from '@/layout/ListPane';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly, parseMoment } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import { ListRow, ListSkeleton } from '@/shared/ui/list-row';
import { EmptyState, ScreenState } from '@/shared/ui/screen-state';
import { SearchFilter } from '@/shared/ui/search-filter';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';

import type { BlacklistEntryOut, BlacklistQuery, BlacklistStatus } from '../api/blacklist';
import { ENTRY_FORMS } from '../dictionaries';
import { useBlacklistQuery } from '../queries';
import type { HiringRoute } from '../route';
import {
  BLANK,
  FILTER_REGION,
  FILTER_STATUS,
  PANEL_NEW_ENTRY,
  hiringHref,
  openPanelHref,
  setFilter,
} from '../route';

/** Состояния записи в отборе. Названия сервер даёт только для существующих строк. */
const STATUS_FILTER_TITLES: Readonly<Record<BlacklistStatus, string>> = {
  active: 'Действует',
  lifted: 'Снята',
};

/** Цвет метки. Слово несёт смысл, цвет его дублирует и не заменяет. */
const STATUS_COLORS: Readonly<Record<BlacklistStatus, 'red' | 'gray'>> = {
  active: 'red',
  lifted: 'gray',
};

/**
 * Есть ли в записи хоть что-то, по чему человека можно узнать.
 *
 * Не только имя: запись, заведённая по одному телефону или по номеру удостоверения, сверку
 * проходит и человека находит. Пустой её делает отсутствие ВСЕХ признаков сразу.
 */
export function hasPersonalData(entry: BlacklistEntryOut): boolean {
  return [
    entry.last_name,
    entry.first_name,
    entry.middle_name,
    entry.birth_date,
    entry.phone_normalized,
    entry.passport_series,
    entry.passport_number,
    entry.guard_license_no,
  ].some((значение) => (значение ?? '').trim() !== '');
}

/**
 * Чем подписать запись в списке и в шапке карточки.
 *
 * Три разных факта, и слипаться им нельзя (правило 7). Есть ФИО — показываем его. ФИО нет, а
 * телефон есть — человек в записи опознан, просто не по имени. Нет ни одного признака — это
 * запись без персональных данных, и сверка по ней никого не найдёт; сказать об этом словами
 * важнее, чем показать пустую строку.
 */
export function entryTitle(entry: BlacklistEntryOut): string {
  const фио = [entry.last_name, entry.first_name, entry.middle_name]
    .map((часть) => (часть ?? '').trim())
    .filter((часть) => часть !== '')
    .join(' ');
  if (фио !== '') {
    return фио;
  }
  const телефон = (entry.phone_normalized ?? '').trim();
  if (телефон !== '') {
    return телефон;
  }
  return hasPersonalData(entry) ? 'Имя в записи не указано' : 'Без персональных данных';
}

/**
 * Отбор из адреса, переведённый в параметры запроса. Единственное место, где адрес
 * превращается в запрос: два таких места разъезжаются, и человек видит в списке не то, что
 * написано в его же ссылке.
 */
export function queryFromRoute(search: SearchState): BlacklistQuery {
  return {
    status: search.filters[FILTER_STATUS],
    region: search.filters[FILTER_REGION],
    q: search.q,
  };
}

export function BlacklistList({
  route,
}: {
  route: HiringRoute;
}) {
  const navigate = useNavigate();
  const { search, rowId } = route;

  const params = queryFromRoute(search);
  const query = useBlacklistQuery(params);

  function apply(next: SearchState) {
    // replace: в истории браузера один шаг на подбор отбора, а не двадцать.
    navigate(hiringHref('blacklist', next, rowId), { replace: true });
  }

  const narrowed =
    search.q !== undefined ||
    search.filters[FILTER_STATUS] !== undefined ||
    search.filters[FILTER_REGION] !== undefined;

  const поиск = (
    <SearchFilter
      label='Поиск по чёрному списку: фамилия или телефон'
      placeholder='Фамилия или телефон'
      value={search.q ?? ''}
      onApply={(value) => apply({ ...search, q: value === '' ? undefined : value })}
    />
  );

  const наложено = [
    search.filters[FILTER_STATUS] === undefined ? null : 'состояние записи',
    search.filters[FILTER_REGION] === undefined ? null : 'регион',
  ].filter((имя): имя is string => имя !== null);

  const filters = (
    <>
      <SelectField
        id='blacklist-status'
        label='Состояние записи'
        value={search.filters[FILTER_STATUS] ?? NO_VALUE}
        onChange={(value) =>
          apply(setFilter(search, FILTER_STATUS, value === NO_VALUE ? undefined : value))
        }
        options={(Object.keys(STATUS_FILTER_TITLES) as BlacklistStatus[]).map((code) => ({
          value: code,
          label: STATUS_FILTER_TITLES[code],
        }))}
        noneLabel='Действующие и снятые'
        placeholder='Действующие и снятые'
        // Пояснение появляется только под включённым отбором: объяснять разницу тому, кто
        // ею не отбирает, незачем, а постоянные три строки текста сдвигают вниз весь список.
        hint={
          search.filters[FILTER_STATUS] === 'lifted'
            ? 'Снятая запись остаётся в списке вместе с причиной снятия: сверка её больше не находит.'
            : undefined
        }
      />

      <RegionFilter
        value={search.filters[FILTER_REGION]}
        onApply={(value) => apply(setFilter(search, FILTER_REGION, value))}
      />
    </>
  );

  return (
    <ListPane
      caption={
        query.data === undefined ? 'Чёрный список' : countLabel(query.data.total, ENTRY_FORMS)
      }
      sliceAt={parseMoment(query.data?.generated_at)}
      search={поиск}
      filters={filters}
      appliedFilters={наложено}
      width='имена'
    >
      <ScreenState
        data={query.data}
        error={query.error}
        subject='чёрный список'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={<ListSkeleton />}
        empty={
          <EmptyState
            icon={RiUserForbidLine}
            title={narrowed ? 'Под этот отбор записей нет' : 'В чёрном списке пока никого нет'}
            description={
              narrowed
                ? 'Список пуст из-за поиска, состояния или региона, а не потому, что записей нет вовсе. Снимите отбор — и увидите остальные.'
                : 'Готового чёрного списка заказчик не передавал: список наполняется вручную и разбором выгрузок групп «Нарушители» и «Кандидаты» из мессенджера.'
            }
            // Действие здесь ЕСТЬ, и это не дубль правой колонки. Кнопка «Внести в чёрный
            // список» живёт внизу списка, но при пустом списке её на экране не остаётся
            // вовсе — пустое состояние рисуется ВМЕСТО строк. Правая колонка при этом молчит
            // намеренно: два приглашения к одному действию разными словами человек читает
            // как два разных действия.
            action={
              narrowed ? (
                <LinkButton.Root variant='primary' size='medium' underline asChild>
                  <Link to={hiringHref('blacklist', { ...search, q: undefined, filters: {} })}>
                    Снять отбор
                  </Link>
                </LinkButton.Root>
              ) : (
                <>
                  <LinkButton.Root variant='primary' size='medium' underline asChild>
                    <Link to={openPanelHref(route, PANEL_NEW_ENTRY, BLANK)}>
                      Внести первую запись
                    </Link>
                  </LinkButton.Root>
                  <LinkButton.Root variant='gray' size='medium' underline asChild>
                    <Link to={hiringHref('drafts', { filters: {}, panels: [] })}>
                      Принять выгрузку из мессенджера
                    </Link>
                  </LinkButton.Root>
                </>
              )
            }
          />
        }
      >
        {(data) => (
          <div className='flex flex-col'>
            {data.items.map((entry) => (
              <ListRow
                key={entry.id}
                to={hiringHref('blacklist', search, entry.id)}
                selected={entry.id === rowId}
                title={entryTitle(entry)}
                // Состояние стоит меткой справа и в подписи НЕ повторяется. В подписи — три
                // вещи, которые отвечают на «за что и до каких пор»: причина, регион и срок
                // хранения. Срок здесь не украшение: после этой даты запись уничтожается, и
                // человек снова проходит сверку чистым.
                subtitle={[
                  entry.reason_title,
                  (entry.region ?? '').trim() === '' ? 'регион не указан' : entry.region,
                  `до ${formatDateOnly(entry.retention_until)}`,
                ].join(' · ')}
                badge={
                  <Badge.Root
                    variant='lighter'
                    color={STATUS_COLORS[entry.status] ?? 'gray'}
                    size='medium'
                  >
                    {entry.status_title}
                  </Badge.Root>
                }
              />
            ))}

            {data.items.length < data.total && (
              <div className='px-4 py-2'>
                <Hint.Root>
                  Показаны первые {data.items.length} из {data.total}. Сузьте отбор — иначе человек,
                  которого вы ищете, может остаться за пределами страницы.
                </Hint.Root>
              </div>
            )}

            <div className='flex justify-center p-3'>
              {/* Кнопка не сереет без права: заведение записи — кадровое решение, и отказ
                  объясняет сама форма, а выключенная кнопка не доводит нажатие до
                  обработчика и молчит (правило 10). */}
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                onClick={() => navigate(openPanelHref(route, PANEL_NEW_ENTRY, BLANK))}
              >
                <Button.Icon as={RiAddLine} />
                Внести в чёрный список
              </Button.Root>
            </div>
          </div>
        )}
      </ScreenState>
    </ListPane>
  );
}

/** Пауза перед записью региона в адрес. Та же, что у поиска над списком. */
const REGION_DEBOUNCE_MS = 300;

/**
 * Отбор по региону. Поле текстовое, потому что справочника регионов на сервере нет: сервер
 * сравнивает то, что записано в самой записи.
 *
 * Введённое уезжает в адрес не на каждой букве: «Ханты-Мансийский автономный округ» это
 * тридцать с лишним нажатий, и адрес менялся бы столько же раз — после такого кнопка «назад»
 * в браузере становится непроходимой.
 */
function RegionFilter({
  value,
  onApply,
}: {
  value: string | undefined;
  onApply: (value: string | undefined) => void;
}) {
  const [draft, setDraft] = useState(value ?? '');
  const applyRef = useRef(onApply);

  useEffect(() => {
    applyRef.current = onApply;
  });

  // Адрес поменялся снаружи (открыли ссылку, нажали «назад») — поле догоняет адрес.
  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    const текущее = value ?? '';
    if (draft.trim() === текущее) {
      return;
    }
    const timer = window.setTimeout(() => {
      applyRef.current(draft.trim() === '' ? undefined : draft.trim());
    }, REGION_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, value]);

  return (
    <TextField
      id='blacklist-region'
      label='Регион'
      value={draft}
      onChange={setDraft}
      placeholder='Ханты-Мансийский АО'
      hint='Сравнивается с регионом, записанным в самой записи: список общий по всей группе, и один человек может быть внесён в другом регионе.'
    />
  );
}
